
import Vue from 'vue'
import config from '@root/config'
import { isCallbotWidgetEnabled } from '@/helpers/callbotWidget'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import IntegrationRequestSidebar from '@/modules/marketplace/components/IntegrationRequestSidebar.vue'
import CreateCallbotDialog from './CreateCallbotDialog.vue'
import CallbotClientSidebar from './CallbotClientSidebar.vue'
import EditCallbotDialog from './EditCallbotDialog.vue'
import CallbotAnalytics from './CallbotAnalytics.vue'
import AudioLibrary from './AudioLibrary.vue'
import CallbotTelephony from './CallbotTelephony.vue'
import { formatRub, runErrorText } from './telephony'
import { redialLabel } from './audioLibrary'
import {
    deleteCallbotRun,
    getCallbotConfiguration,
    getCallbotRun,
    getCallbotRunRecipients,
    getCallbotRuns,
    getCallbotTelephonyBalance,
    getCallbotTasks,
    resumeCallbotRun,
    stopCallbotRun,
    updateCallbotTask,
} from '@/api/callbot'
import {
    CallbotConfiguration,
    CallbotRecipient,
    CallbotMetrics,
    CallbotMaterial,
    CallbotRun,
    CallbotRunStatus,
    CallbotTask,
    CallbotTaskAction,
    CallbotTaskFilter,
    CallbotTaskGroup,
} from '@/types/callbot'
import { MarketplaceIntegration } from '@/types/marketplace'
import { checkAccess } from '@/utils'

type RunFilter = 'all' | 'active' | 'completed' | 'stopped'
type MainTab = 'campaigns' | 'audio' | 'tasks' | 'analytics' | 'telephony'
type DetailTab = 'results' | 'recipients' | 'settings'

type TaskGroupOption = {
    value: CallbotTaskGroup
    title: string
    description: string
}

const EMPTY_METRICS: CallbotMetrics = {
    processedRecipients: 0,
    finishedAttempts: 0,
    pressed1: 0,
    pressed2: 0,
    noAnswer: 0,
    voicemail: 0,
    noInput: 0,
    providerErrors: 0,
    costRub: 0,
}

export default Vue.extend({
    name: 'Callbot',
    components: {
        Breadcrumbs,
        AudioLibrary,
        CallbotTelephony,
        CallbotAnalytics,
        CallbotClientSidebar,
        CreateCallbotDialog,
        EditCallbotDialog,
        IntegrationRequestSidebar,
    },
    data() {
        return {
            editDialog: false,
            editRun: null as CallbotRun | null,
            draftRun: null as CallbotRun | null,
            clientSidebar: false,
            clientTask: null as CallbotTask | null,
            deleteDialog: false,
            deleteTarget: null as CallbotRun | null,
            deleting: false,
            callbotConfiguration: null as CallbotConfiguration | null,
            runs: [] as CallbotRun[],
            selected: null as CallbotRun | null,
            loading: false,
            detailsLoading: false,
            detailsVisible: false,
            detailTab: 'results' as DetailTab,
            recipients: [] as CallbotRecipient[],
            recipientTotal: 0,
            recipientLoading: false,
            recipientError: '',
            recipientNextCursor: null as string | null,
            recipientRequestId: 0,
            mainTab: 'campaigns' as MainTab,
            topupReturned: false,
            projectBalance: null as number | null,
            createMaterial: null as CallbotMaterial | null,
            activeTaskGroup: 'clarify' as CallbotTaskGroup,
            taskFilter: 'all' as CallbotTaskFilter,
            tasks: [] as CallbotTask[],
            taskCounts: {
                clarify: 0,
                deferred: 0,
                recorded: 0,
                do_not_call: 0,
                closed: 0,
            } as Record<CallbotTaskGroup, number>,
            taskLoading: false,
            taskError: '',
            taskNextCursor: null as string | null,
            taskUpdatingId: null as string | null,
            actionRunId: null as string | null,
            taskRequestId: 0,
            createDialog: false,
            serviceDialog: false,
            requestSidebarOpened: false,
            serviceIntegration: {
                id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                name: 'Аудиорассылки DH',
            } as MarketplaceIntegration,
            accessResolved: false,
            liveStarted: false,
            error: '',
            filter: 'all' as RunFilter,
            refreshTimer: null as number | null,
            filterOptions: [
                { value: 'all' as RunFilter, label: 'Все' },
                { value: 'active' as RunFilter, label: 'Запущенные' },
                { value: 'completed' as RunFilter, label: 'Завершенные' },
                { value: 'stopped' as RunFilter, label: 'Стоп' },
            ],
            taskFilterOptions: [
                { value: 'all' as CallbotTaskFilter, label: 'Все' },
                { value: 'today' as CallbotTaskFilter, label: 'Сегодня' },
                { value: 'overdue' as CallbotTaskFilter, label: 'Просрочено' },
            ],
            taskGroupOptions: [
                {
                    value: 'clarify' as CallbotTaskGroup,
                    title: 'Уточните статус',
                    description: 'Тёплые обращения',
                },
                {
                    value: 'deferred' as CallbotTaskGroup,
                    title: 'Отложенная коммуникация',
                    description: 'Связаться позже',
                },
                {
                    value: 'recorded' as CallbotTaskGroup,
                    title: 'Записаны',
                    description: 'Подтверждено CRM',
                },
                {
                    value: 'do_not_call' as CallbotTaskGroup,
                    title: 'Не звонить',
                    description: 'Исключены из рассылок',
                },
                {
                    value: 'closed' as CallbotTaskGroup,
                    title: 'Не записан',
                    description: 'Отказались от записи',
                },
            ] as TaskGroupOption[],
            servicePoints: [
                'Подготовка базы',
                'Аудиоролик',
                'Сценарий звонка',
                'Инструкции и рекомендации команде',
            ],
            serviceBenefits: [
                {
                    title: 'Подготовка базы',
                    description: 'Сформируем и проверим аудиторию для аудиорассылки.',
                },
                {
                    title: 'Аудиоролик',
                    description: 'Подготовим текст, поможем с записью и проверим ролик перед запуском.',
                },
                {
                    title: 'Сценарий звонка',
                    description: 'Настроим расписание, повторные попытки и соединение с номером салона.',
                },
                {
                    title: 'Инструкции и рекомендации команде',
                    description: 'Объясним, как принимать тёплые обращения и фиксировать результат.',
                },
            ],
            servicePlans: [
                { period: '1 месяц', price: '10 000 ₽/мес' },
                { period: '3 месяца', price: '9 000 ₽/мес' },
                { period: '6 месяцев', price: '8 000 ₽/мес' },
                { period: '12 месяцев', price: '7 000 ₽/мес' },
            ],
        }
    },
    computed: {
        projectId(): string {
            return this.$route.params.id
        },
        project(): any {
            return this.$store.getters.projectById(this.projectId) || {}
        },
        hasCallbotAccess(): boolean {
            return isCallbotWidgetEnabled(this.project)
        },
        breadcrumbs(): any[] {
            return [
                { text: 'Ваши проекты', to: '/projects' },
                { text: this.project.name || 'Проект', to: `/project/${this.projectId}` },
                { text: 'Коммуникации', to: `/project/${this.projectId}`, disabled: false },
                { text: 'Аудиорассылки DH', to: '', disabled: true },
            ]
        },
        canManage(): boolean {
            return this.checkAccess(this.projectId, 'admin')
        },
        // Наблюдатели не управляют кампанией (запуск/остановка/аудио), но
        // разбор входящих лидов — их основная задача, поэтому кнопки
        // «Записан»/«Отложить»/«Не записан»/«Не звонить» доступны им так же,
        // как чтение (backend: routes/callbot.ts, taskManageRoles).
        canManageTasks(): boolean {
            return this.checkAccess(this.projectId, 'observer')
        },
        hasActiveRuns(): boolean {
            return this.runs.some(run => ['starting', 'running', 'stopping'].includes(run.status))
        },
        metrics(): CallbotMetrics {
            return this.selected?.metrics || EMPTY_METRICS
        },
        activeTask(): {
            value: CallbotTaskGroup
            title: string
            description: string
            count: number
        } {
            return this.taskGroups.find(group => group.value === this.activeTaskGroup) || this.taskGroups[0]
        },
        taskGroups(): Array<TaskGroupOption & { count: number }> {
            return this.taskGroupOptions.map(group => ({
                ...group,
                count: this.taskCounts[group.value] || 0,
            }))
        },
        deleteCopy(): string {
            if (!this.deleteTarget) return ''
            const active = ['starting', 'running', 'stopping'].includes(
                this.deleteTarget.status
            )
            return active
                ? 'Рассылка ещё звонит. Сначала остановите её, иначе удалить не получится.'
                : `Удалить «${this.deleteTarget.name}»? Она исчезнет из списка и из выбора кампании в аналитике, но её звонки и расходы останутся в итогах за период.`
        },
        filteredRuns(): CallbotRun[] {
            return this.runs.filter(run => {
                const active = ['accepting_recipients', 'starting', 'running', 'stopping'].includes(
                    run.status
                )
                return (
                    this.filter === 'all' ||
                    (this.filter === 'active' && active) ||
                    (this.filter === 'completed' && run.status === 'completed') ||
                    (this.filter === 'stopped' && run.status === 'stopped')
                )
            })
        },
        runsLabel(): string {
            const count = this.filteredRuns.length
            const mod10 = count % 10
            const mod100 = count % 100
            const word =
                mod10 === 1 && mod100 !== 11
                    ? 'рассылка'
                    : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)
                    ? 'рассылки'
                    : 'рассылок'
            return `${count} ${word}`
        },
        scheduleLabel(): string {
            if (!this.selected?.schedule) return '—'
            const labels: Record<string, string> = {
                morning: '09:00–12:00',
                day: '12:00–17:00',
                evening: '17:00–20:00',
            }
            const windows = this.selected.schedule.slots.map(slot => labels[slot])
            if (this.selected.schedule.customWindow) {
                windows.push(
                    `${this.selected.schedule.customWindow.from}–${this.selected.schedule.customWindow.to}`
                )
            }
            return windows.join(', ')
        },
        retryLabel(): string {
            if (!this.selected?.schedule) return '—'
            return redialLabel(this.selected.schedule.maxAttempts, this.selected.schedule.retryIntervalMinutes)
        },
        costPerLead(): string {
            if (!this.metrics.pressed1) return '—'
            return this.formatMoney(this.metrics.costRub / this.metrics.pressed1)
        },
    },
    watch: {
        project: {
            handler() {
                if (!this.project?.id) return
                this.accessResolved = true
                if (this.hasCallbotAccess) this.startLive()
            },
            immediate: true,
        },
    },
    beforeDestroy() {
        if (this.refreshTimer != null) window.clearInterval(this.refreshTimer)
        this.taskRequestId += 1
        this.recipientRequestId += 1
    },
    methods: {
        checkAccess,
        startLive() {
            if (this.liveStarted) return
            this.liveStarted = true
            // Возврат со страницы ЮKassa: billing отправляет сюда с ?tab=telephony.
            const query = this.$route?.query ?? {}
            if (query.tab === 'telephony') {
                this.mainTab = 'telephony'
                this.topupReturned = query.topup === 'success'
            }
            this.loadRuns()
            this.loadProjectBalance()
            this.refreshTimer = window.setInterval(this.refresh, 10_000)
        },
        buyModule() {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.zvonoBot
            if (!moduleId || !this.projectId) return
            this.$router.push(`/project/${this.projectId}/marketplace/integration/${moduleId}`).catch(() => {})
        },
        runErrorText,
        formatRub,
        async selectMainTab(tab: MainTab) {
            this.mainTab = tab
            this.error = ''
            if (tab === 'tasks') await this.loadTasks()
        },
        async selectTaskGroup(group: CallbotTaskGroup) {
            if (this.activeTaskGroup === group && this.taskFilter === 'all') return
            this.activeTaskGroup = group
            this.taskFilter = 'all'
            await this.loadTasks()
        },
        async selectTaskFilter(filter: CallbotTaskFilter) {
            if (this.taskFilter === filter) return
            this.taskFilter = filter
            await this.loadTasks()
        },
        async loadTasks(append = false) {
            if (append && this.taskLoading) return
            const requestId = ++this.taskRequestId
            this.taskLoading = true
            this.taskError = ''
            const result = await getCallbotTasks(
                this.projectId,
                this.activeTaskGroup,
                this.taskFilter,
                40,
                append ? this.taskNextCursor || undefined : undefined
            )
            if (requestId !== this.taskRequestId) return
            this.taskLoading = false
            if (result.error || !result.data) {
                this.taskError = result.error?.message || 'Не удалось загрузить задачи'
                return
            }
            this.taskCounts = result.data.groups
            this.tasks = append ? [...this.tasks, ...result.data.items] : result.data.items
            this.taskNextCursor = result.data.nextCursor
        },
        async loadMoreTasks() {
            if (!this.taskNextCursor) return
            await this.loadTasks(true)
        },
        async selectDetailTab(tab: DetailTab) {
            this.detailTab = tab
            if (tab === 'recipients' && this.recipients.length === 0) {
                await this.loadRecipients()
            }
        },
        async loadRecipients(append = false) {
            if (!this.selected || (append && this.recipientLoading)) return
            const runId = this.selected.runId
            const requestId = ++this.recipientRequestId
            this.recipientLoading = true
            this.recipientError = ''
            const result = await getCallbotRunRecipients(
                this.projectId,
                runId,
                40,
                append ? this.recipientNextCursor || undefined : undefined
            )
            if (requestId !== this.recipientRequestId || this.selected?.runId !== runId) return
            this.recipientLoading = false
            if (result.error || !result.data) {
                this.recipientError = result.error?.message || 'Не удалось загрузить получателей'
                return
            }
            this.recipientTotal = result.data.total
            this.recipients = append
                ? [...this.recipients, ...result.data.items]
                : result.data.items
            this.recipientNextCursor = result.data.nextCursor
        },
        async loadMoreRecipients() {
            if (!this.recipientNextCursor) return
            await this.loadRecipients(true)
        },
        async changeTaskStatus(task: CallbotTask, status: CallbotTaskAction) {
            if (this.taskUpdatingId) return
            this.taskUpdatingId = task.taskId
            this.taskError = ''
            const result = await updateCallbotTask(this.projectId, task.taskId, status)
            this.taskUpdatingId = null
            if (result.error) {
                this.taskError = result.error.message || 'Не удалось обновить задачу'
                return
            }
            await this.loadTasks()
        },
        async loadRuns() {
            this.loading = true
            this.error = ''
            const result = await getCallbotRuns(this.projectId)
            this.loading = false
            if (result.error || !result.data) {
                this.error = result.error?.message || 'Не удалось загрузить аудиорассылки'
                return
            }
            this.runs = result.data.items
            if (this.detailsVisible && this.selected) {
                const current = this.runs.find(run => run.runId === this.selected?.runId)
                if (current) await this.loadRunDetails(current)
            }
        },
        async loadRunDetails(run: CallbotRun) {
            this.selected = run
            this.detailsLoading = true
            const result = await getCallbotRun(this.projectId, run.runId)
            this.detailsLoading = false
            if (result.data) this.selected = result.data
            else this.error = result.error?.message || 'Не удалось загрузить детали аудиорассылки'
        },
        async openRun(run: CallbotRun) {
            this.detailsVisible = true
            this.detailTab = 'results'
            this.recipients = []
            this.recipientTotal = run.recipientCount
            this.recipientNextCursor = null
            this.recipientError = ''
            this.recipientRequestId += 1
            await this.loadRunDetails(run)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        closeDetails() {
            this.detailsVisible = false
            this.selected = null
            this.recipients = []
            this.recipientTotal = 0
            this.recipientNextCursor = null
            this.recipientError = ''
            this.recipientRequestId += 1
            this.error = ''
        },
        openServiceRequest() {
            this.serviceDialog = false
            this.requestSidebarOpened = true
        },
        async loadProjectBalance() {
            const { data } = await getCallbotTelephonyBalance(this.projectId)
            if (data) this.projectBalance = data.balanceRub
        },
        async refresh() {
            this.loadProjectBalance()
            if (this.mainTab === 'tasks') await this.loadTasks()
            if (this.runs.some(run => ['starting', 'running', 'stopping'].includes(run.status))) {
                await this.loadRuns()
                if (this.detailsVisible && this.detailTab === 'recipients') {
                    await this.loadRecipients()
                }
            }
        },
        async onCreated(run: CallbotRun) {
            await this.loadRuns()
            const created = this.runs.find(item => item.runId === run.runId) || run
            await this.openRun(created)
        },
        openClientCard(task: CallbotTask) {
            this.clientTask = task
            this.clientSidebar = true
        },
        async onSidebarAction(payload: { task: CallbotTask; status: CallbotTaskAction }) {
            await this.changeTaskStatus(payload.task, payload.status)
            // Лид ушёл из текущей группы — держать карточку открытой незачем.
            this.clientSidebar = false
            this.clientTask = null
        },
        openDraft(run: CallbotRun) {
            this.draftRun = run
            this.createDialog = true
        },
        askDelete(run: CallbotRun) {
            this.deleteTarget = run
            this.deleteDialog = true
        },
        async confirmDelete() {
            if (!this.deleteTarget || this.deleting) return
            this.deleting = true
            const result = await deleteCallbotRun(
                this.projectId,
                this.deleteTarget.runId
            )
            this.deleting = false
            if (result.error) {
                this.error = 'Не удалось удалить аудиорассылку'
                this.deleteDialog = false
                return
            }
            const removedId = this.deleteTarget.runId
            this.deleteDialog = false
            this.deleteTarget = null
            if (this.selected && this.selected.runId === removedId) {
                this.selected = null
                this.detailsVisible = false
            }
            await this.loadRuns()
        },
        async openEdit(run: CallbotRun) {
            this.editRun = run
            this.editDialog = true
            if (this.callbotConfiguration) return
            // Список каналов уведомлений нужен только диалогу, поэтому
            // тянем его лениво и один раз.
            const result = await getCallbotConfiguration(this.projectId)
            if (result.data) this.callbotConfiguration = result.data
        },
        openCreate() {
            this.draftRun = null
            this.createMaterial = null
            this.createDialog = true
        },
        // «Создать рассылку» из карточки одобренного ролика: диалог создания
        // открывается с уже выбранной записью.
        openCreateWithMaterial(material: CallbotMaterial) {
            this.mainTab = 'campaigns'
            this.draftRun = null
            this.createMaterial = material
            this.createDialog = true
        },
        onSettingsSaved(updated: CallbotRun) {
            const index = this.runs.findIndex(run => run.runId === updated.runId)
            if (index >= 0) this.$set(this.runs, index, updated)
            if (this.selected && this.selected.runId === updated.runId) {
                this.selected = updated
            }
            this.loadRuns()
        },
        async changeRunState(run: CallbotRun, action: 'stop' | 'resume') {
            if (this.actionRunId) return
            this.actionRunId = run.runId
            this.error = ''
            const command = action === 'stop' ? stopCallbotRun : resumeCallbotRun
            const result = await command(this.projectId, run.runId)
            this.actionRunId = null
            if (result.error || !result.data) {
                const message = typeof result.error === 'string' ? result.error : result.error?.message
                // Сервис не возобновляет рассылку без денег на телефонию.
                if (action !== 'stop' && /telephony balance/i.test(message || '')) {
                    this.error = runErrorText('INSUFFICIENT_BALANCE')
                    return
                }
                this.error =
                    message ||
                    (action === 'stop'
                        ? 'Не удалось поставить аудиорассылку на паузу'
                        : 'Не удалось продолжить аудиорассылку')
                return
            }
            const index = this.runs.findIndex(item => item.runId === run.runId)
            if (index >= 0) this.$set(this.runs, index, result.data)
            if (this.selected?.runId === run.runId) this.selected = result.data
            await this.loadRuns()
        },
        activationName(run: CallbotRun) {
            return run.activation?.name || run.audience?.name || 'Активация'
        },
        statusLabel(status: CallbotRunStatus) {
            return (
                {
                    accepting_recipients: 'Черновик',
                    starting: 'Запускается',
                    running: 'Запущен',
                    stopping: 'Останавливается',
                    stopped: 'Стоп',
                    completed: 'Финиш',
                    error: 'Ошибка',
                }[status] || status
            )
        },
        recipientStatusLabel(recipient: CallbotRecipient) {
            const result = recipient.lastResult || recipient.latestAttempt?.result
            if (result) {
                return (
                    {
                        pressed_1: 'Нажал 5 — тёплый лид',
                        pressed_2: 'Нажал 3 — больше не звонить',
                        no_answer: 'Не дозвонились',
                        voicemail: 'Автоответчик',
                        hangup_no_input: 'Ответил без выбора',
                        provider_error: 'Ошибка телефонии',
                    }[result] || result
                )
            }
            return (
                {
                    pending: 'Ожидает звонка',
                    submitting: 'Создаём звонок',
                    in_progress: 'Звонок идёт',
                    retry_wait: 'Ожидает повтора',
                    done: 'Обработан',
                    excluded: 'Исключён',
                    error: 'Ошибка',
                }[recipient.state] || recipient.state
            )
        },
        recipientStatusTone(recipient: CallbotRecipient) {
            const result = recipient.lastResult || recipient.latestAttempt?.result
            if (result === 'pressed_1') return 'success'
            if (result === 'pressed_2' || result === 'provider_error') return 'danger'
            if (recipient.state === 'in_progress' || recipient.state === 'submitting') return 'active'
            if (recipient.state === 'done') return 'success'
            if (recipient.state === 'excluded' || recipient.state === 'error') return 'danger'
            return 'neutral'
        },
        attemptsLabel(count: number) {
            const mod10 = count % 10
            const mod100 = count % 100
            const word =
                mod10 === 1 && mod100 !== 11
                    ? 'попытка'
                    : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)
                    ? 'попытки'
                    : 'попыток'
            return `${count} ${word}`
        },
        formatPhone(value: string) {
            const digits = String(value || '').replace(/\D/g, '')
            if (digits.length === 11 && ['7', '8'].includes(digits[0])) {
                return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(
                    7,
                    9
                )}-${digits.slice(9)}`
            }
            return value.startsWith('+') ? value : `+${value}`
        },
        formatNumber(value?: number) {
            return new Intl.NumberFormat('ru-RU').format(value || 0)
        },
        formatMoney(value?: number) {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                maximumFractionDigits: 2,
            }).format(value || 0)
        },
        formatDate(value: string) {
            return new Intl.DateTimeFormat('ru-RU').format(new Date(value))
        },
        formatDateTime(value: string) {
            return new Intl.DateTimeFormat('ru-RU', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(new Date(value))
        },
        formatTaskPhone(phone: string) {
            const digits = phone.replace(/\D/g, '')
            if (digits.length !== 11) return phone
            return `+${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(
                7,
                9
            )}-${digits.slice(9)}`
        },
        taskDateLabel(task: CallbotTask) {
            const value = task.dueAt || task.callFinishedAt || task.updatedAt
            if (!value) return '—'
            const prefix = task.group === 'deferred' ? 'связаться ' : ''
            return `${prefix}${new Intl.DateTimeFormat('ru-RU', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
            }).format(new Date(value))}`
        },
        taskStatusLabel(task: CallbotTask) {
            if (task.group === 'recorded') return 'Записан'
            if (task.group === 'deferred') return 'Отложено администратором'
            if (task.group === 'do_not_call') {
                return task.result === 'pressed_2' ? 'Нажал 3' : 'Исключён администратором'
            }
            if (task.group === 'closed') return 'Не записан'
            return task.transferConnected
                ? 'Разговор с администратором состоялся'
                : 'Администратор не ответил'
        },
    },
})
